import React from "react";
import Loader from "./Loader";
import { useTable } from "react-table";

// Define types for data structure
interface EwiLoanDetail {
  name: string;
  loan_count: string;
  percentage_ewi_loans_count: number;
  total_ewi_disbursed_amount: string;
  percentage_ewi_disb_amount: number;
}

interface PeriodData {
  period: string;
  start_date: string;
  end_date: string;
  ewi_loan_details: EwiLoanDetail[];
}

interface TableComponentProps {
  data: PeriodData[];
  loading: boolean;
}

export const TableBranchState: React.FC<TableComponentProps> = ({ data, loading }) => {

  if (loading) return (
    <div className="d-flex justify-content-center align-items-center" style={{"minHeight": "50vh"}}>
      <Loader />
    </div>
  )
  if (!data || data.length === 0) return (
    <p>No data available for the selected criteria</p>
  )

  // Extract all periods dynamically
  const periods = data?.map((entry) => entry.period);

  // Define subtypes (keys)
  const subTypes: (keyof EwiLoanDetail)[] = [
    "loan_count",
    "percentage_ewi_loans_count",
    "total_ewi_disbursed_amount",
    "percentage_ewi_disb_amount",
  ];

  const keyLabelingData: any = {
    loan_count: "# Loans",
    percentage_ewi_loans_count : "% Loans",
    total_ewi_disbursed_amount : "Disb Amt",
    percentage_ewi_disb_amount : "% Disb Amt"
  }

  return (
    <table className="table" style={{margin: "20px"}}>
      {/* Table Header */}
      <thead>
        <tr style={{ fontSize: 12, fontWeight: 300}}>
          <th className="tableHead" style={{"minWidth": "130px"}}>Branch State</th>
          <th className="tableHead" style={{"minWidth": "130px"}}></th>
          {periods?.map((period, index) => (
            <th className="tableHead" style={{"minWidth": "100px"}} key={index}>{period}</th>
          ))}
        </tr>
      </thead>

      {/* Table Body */}
      <tbody style={{ fontSize: 13, fontWeight: 400}}>
        {data[0]?.ewi_loan_details?.map((ewiData, rowIndex) => (
          <React.Fragment key={rowIndex}>
            {subTypes?.map((subType, subIndex) => (
              <tr key={`${rowIndex}-${subIndex}`}>
                {subIndex === 0 && <td rowSpan={subTypes.length}>{ewiData.name}</td>}
                <td className="border text-center align-middle"
                  style={{
                    minWidth: "100px",
                    backgroundColor: ["loan_count", "percentage_ewi_disb_amount"].includes(subType) ? "#e9ecef" : "white",
                  }}
                >{keyLabelingData[subType]}</td>
                {data?.map((periodData, periodIndex) => (
                  <td key={`${rowIndex}-${subIndex}-${periodIndex}`} 
                    style={{
                      minWidth: "100px",
                      backgroundColor: ["loan_count", "percentage_ewi_disb_amount"].includes(subType) ? "#e9ecef" : "white",
                    }}
                  >
                    {periodData.ewi_loan_details[rowIndex]?.[subType] ?? "-"}
                  </td>
                ))}
              </tr>
            ))}
          </React.Fragment>
        ))}
      </tbody>
    </table>
  );
};

// Define types for data structure
interface EmiLoanDetail {
  name: string;
  loan_count: string;
  percentage_emi_loans_count: number;
  total_emi_disbursed_amount: string;
  percentage_emi_disb_amount: number;
}

interface PeriodData {
  period: string;
  start_date: string;
  end_date: string;
  emi_loan_details: EmiLoanDetail[];
}

interface TableComponentProps2 {
  data: PeriodData[];
  loading: boolean;
}

export const TableBranchStateEMI: React.FC<TableComponentProps2> = ({ data, loading }) => {
  if (loading) return (
     <div className="d-flex justify-content-center align-items-center" style={{"minHeight": "50vh"}}>
       <Loader />
     </div>
  );
  if (!data || data.length === 0) return (
    <p>No data available for the selected criteria</p>
  )

  // Extract all periods dynamically
  const periods = data?.map((entry) => entry.period);

  // Define subtypes (keys)
  const subTypes: (keyof EmiLoanDetail)[] = [
    "loan_count",
    "percentage_emi_loans_count",
    "total_emi_disbursed_amount",
    "percentage_emi_disb_amount",
  ];

  const keyLabelingData: any = {
    loan_count: "# Loans",
    percentage_emi_loans_count : "% Loans",
    total_emi_disbursed_amount : "Disb Amt",
    percentage_emi_disb_amount : "% Disb Amt"
  }

  return (
    <table className="table" style={{margin: "20px"}}>
      {/* Table Header */}
      <thead>
        <tr style={{ fontSize: 12, fontWeight: 300}}>
          <th className="tableHead" style={{"minWidth": "130px"}}>Branch State</th>
          <th className="tableHead" style={{"minWidth": "130px"}}></th>
          {periods?.map((period, index) => (
            <th className="tableHead" style={{"minWidth": "100px"}} key={index}>{period}</th>
          ))}
        </tr>
      </thead>

      {/* Table Body */}
      <tbody style={{ fontSize: 13, fontWeight: 400}}>
        {data[0]?.emi_loan_details?.map((emiData, rowIndex) => (
          <React.Fragment key={rowIndex}>
            {subTypes?.map((subType, subIndex) => (
              <tr key={`${rowIndex}-${subIndex}`}>
                {subIndex === 0 && <td rowSpan={subTypes.length}>{emiData.name}</td>}
                <td className="border text-center align-middle"
                  style={{
                    minWidth: "100px",
                    backgroundColor: ["loan_count", "percentage_emi_disb_amount"].includes(subType) ? "#e9ecef" : "white",
                  }}
                >{keyLabelingData[subType]}</td>
                {data?.map((periodData, periodIndex) => (
                  <td key={`${rowIndex}-${subIndex}-${periodIndex}`}
                    style={{
                      minWidth: "100px",
                      backgroundColor: ["loan_count", "percentage_emi_disb_amount"].includes(subType) ? "#e9ecef" : "white",
                    }}
                  >
                    {periodData.emi_loan_details[rowIndex]?.[subType] ?? "-"}
                  </td>
                ))}
              </tr>
            ))}
          </React.Fragment>
        ))}
      </tbody>
    </table>
  );
};

interface TableListBranchesProps {
  columns: string[];
  data: any;
}
export const TableListBranches: React.FC<TableListBranchesProps> = ({ columns, data }) => {
  
  if (!data || data.length === 0) return (
     <div className="d-flex justify-content-center align-items-center" style={{"minHeight": "50vh"}}>
       <Loader />
     </div>
  );

  const percentageValues: number[] = [];

    data?.forEach((row: any) => {
      Object.values(row).forEach((value: any) => {
        if (typeof value === "string") {
          // Remove non-numeric characters
          const cleanedValue = value?.replace(/[%M]/g, "").trim(); 
    
          // Ensure cleanedValue is a valid number
          const numericValue = parseFloat(cleanedValue);
          if (!isNaN(numericValue)) {
            percentageValues.push(numericValue);
          } else {
            console.warn("Invalid numeric value:", value, "->", cleanedValue);
          }
        }
      });
    });
  
    // Determine min and max percentages
    const minPercentage = Math.min(...percentageValues);
    const maxPercentage = Math.max(...percentageValues);
    console.log(minPercentage , "minPercentage")
    console.log(maxPercentage , "maxPercentage")

    const getBackgroundColor = (value: string) => {
      // if (!value.includes("%")) return "white"; 
      // if (!value.includes("%") && !value.includes("L")) return "white"; // Default background for non-percentage and non-"M" values

      const numericValue = parseFloat(value?.replace("%", "")?.replace("L", ""));

      console.log(numericValue, "numericValue")
  
      // const numericValue = parseFloat(value.replace("%", ""));
      
      if (percentageValues.length === 1) return "#d15757"; // If only 1 percentage, set max opacity #d15757
  
      // Normalize opacity based on min/max range (0.2 for min, 1 for max)
      const opacity =
        minPercentage === maxPercentage
          ? 1
          : 0 + ((numericValue - minPercentage) / (maxPercentage - minPercentage)) * 0.8;
   
      return `rgba(255, 0, 0, ${opacity})`;
    };

  return (
      <table className="table" style={{margin: "20px"}}>
        <thead>
          <tr style={{ fontSize: 12, fontWeight: 300}}>
            {columns.map((col, index) => (
              <th key={index} className="tableHead" style={{"minWidth": "130px"}}>
                {col}
              </th>
            ))}
          </tr>
        </thead>
        <tbody  style={{ fontSize: 13, fontWeight: 400}}>
          {data.map((row: any, rowIndex: number) => (
            <tr key={rowIndex} className="hover:bg-gray-100">
              {columns.map((col, colIndex) => (
                <td  key={colIndex} className="border px-4 py-2" style={{"background": getBackgroundColor(row[col])}}>
                  {row[col] || "-"}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
  );
};

// Define the type for each row of data
interface DataRow {
  product: string;
  type: string;
  values: (string | number)[];
  rowSpan?: number;
}

// Define props type
interface TableProps {
  data: DataRow[];
  headers: string[];
  loading: boolean;
}

export const ProductTable: React.FC<TableProps> = ({ data, headers, loading }) => {

  if (loading) return (
    <div className="d-flex justify-content-center align-items-center" style={{"minHeight": "50vh"}}>
      <Loader />
    </div>
  );
  if (!data || data.length === 0) return (
    <p>No data available for the selected criteria</p>
  )

  return (
      <table className="table" style={{margin: "20px"}}>
        <thead>
          <tr style={{ fontSize: 12, fontWeight: 300}}>
            {headers.map((header, index) => (
              <th key={index} className="tableHead" style={{"minWidth": "130px"}}>
                {header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody style={{ fontSize: 13, fontWeight: 400}}>
          {data.map((row, rowIndex) => (
            <tr key={rowIndex} >
              {row.rowSpan && rowIndex % 3 === 0 ? (
                <td
                  rowSpan={row.rowSpan}
                  className="border p-2 align-middle"
                  style={{minWidth: "250px"}}
                >
                  {row.product}
                </td>
              ) : null}
              <td className="border p-2 " style={{minWidth: "150px", backgroundColor: rowIndex % 2 === 0 ? "#e9ecef" : "#ffffff"}}>{row.type}</td>
              {row.values.map((value, colIndex) => (
                <td key={colIndex} className="p-2"  style={{backgroundColor: rowIndex % 2 === 0 ? "#e9ecef" : "#ffffff"}}>
                  {value}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
  );
};